import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import { siteInfo } from "@/mocks/site";
import { processSteps, testimonials } from "@/mocks/testimonials";

const principles = [
  {
    icon: "ri-shield-keyhole-line",
    title: "隐私优先",
    desc: "投稿人身份默认脱敏，后台只保留线索内容本身与可选的返联方式。",
  },
  {
    icon: "ri-scales-3-line",
    title: "事实为重",
    desc: "不做收到即发布的堆料站，任何指控都要经过独立信源交叉核实。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续回应",
    desc: "线索进入发酵后不等于结束，状态标签会随核实进度持续更新。",
  },
  {
    icon: "ri-forbid-2-line",
    title: "拒绝交易",
    desc: "不收取任何删帖费用，处置只看证据，不看压力。",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="关于我们 | 511爆料匿名投稿平台的定位与原则"
        description="511爆料是一个面向大众的匿名投稿与瓜料分享平台。了解我们的品牌定位、发展理念、内容原则、审核流程与用户口碑，以及为什么我们坚持隐私优先、事实为重。"
        canonical="https://cqkczx.cn/about"
        keywords="关于511爆料,511爆料平台介绍,匿名投稿平台,内容原则,隐私保护"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于我们 | 511爆料",
            url: "https://cqkczx.cn/about",
            inLanguage: "zh-CN",
            mainEntity: {
              "@type": "Organization",
              name: "511爆料",
              url: "https://cqkczx.cn/",
              email: siteInfo.email,
              description: siteInfo.intro,
              slogan: siteInfo.slogan,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://cqkczx.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "关于我们",
                item: "https://cqkczx.cn/about",
              },
            ],
          },
        ]}
      />

      <section className="w-full bg-background-100/70 border-b border-background-200/70">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-10 md:pb-12">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            关于 {siteInfo.name}
          </h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
            {siteInfo.intro}
          </p>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <Reveal>
            <div className="w-full h-64 md:h-[420px] rounded-lg overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=stylized%20warm%20toned%20editorial%20workspace%20with%20scattered%20anonymous%20tip%20notes%2C%20vintage%20typewriter%20and%20a%20cup%20of%20tea%20on%20a%20wooden%20desk%2C%20soft%20window%20light%2C%20amber%20and%20deep%20red%20palette%2C%20clean%20minimal%20composition%2C%20high%20detail&width=1200&height=900&seq=baoliao-about-team&orientation=landscape"
                alt="511爆料线索整理与审核工作场景"
                title="511爆料 匿名投稿 线索审核"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div>
              <SectionHeading
                eyebrow="我们的定位"
                title="一个认真对待每一条线索的投稿入口"
                desc="511爆料不是八卦工厂，而是一条连接投稿人与核实流程的通道。我们相信，只有当投稿人觉得安全、被听见，线索才有价值。"
              />
              <p className="mt-5 text-sm md:text-base text-foreground-700 leading-loose">
                平台上线以来，我们坚持用人工审核替代算法堆料，用状态标签代替一句“据说”。
                投喂瓜料的方式可以很轻，但核实的过程必须很重。这也是我们与其他内容站最大的不同。
              </p>
              <dl className="mt-8 grid grid-cols-2 gap-4">
                {siteInfo.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="px-4 py-3.5 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <dt className="text-xs text-foreground-500">{stat.label}</dt>
                    <dd className="mt-1 font-heading text-xl font-bold text-primary-600">
                      {stat.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="w-full bg-background-100/60 border-y border-background-200/70">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <Reveal>
            <SectionHeading
              eyebrow="内容原则"
              title="四条不打折扣的底线"
              desc="无论线索来自私信投稿还是投稿表单，都会按同一套原则处理。"
              align="center"
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {principles.map((item, index) => (
              <Reveal key={item.title} delay={index * 80}>
                <div className="h-full p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/80">
                  <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <Reveal>
          <SectionHeading
            eyebrow="线索如何流转"
            title="从提报到发酵，我们做了什么"
            desc="流程透明，是我们对投稿人最基本的交代。"
          />
        </Reveal>
        <ol className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {processSteps.map((step, index) => (
            <Reveal key={step.step} delay={index * 90}>
              <li className="h-full p-5 rounded-lg bg-secondary-50 border border-secondary-200/70">
                <span className="font-heading text-2xl font-bold text-accent-600">{step.step}</span>
                <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </section>

      <section className="w-full bg-background-100/60 border-y border-background-200/70">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <Reveal>
            <SectionHeading
              eyebrow="用户口碑"
              title="投稿人愿意再次投喂，是最大的认可"
              align="center"
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {testimonials.map((item, index) => (
              <Reveal key={item.id} delay={index * 80}>
                <figure className="h-full flex flex-col p-5 rounded-lg bg-background-50 border border-background-200/80">
                  <span className="w-8 h-8 flex items-center justify-center text-accent-500">
                    <i className="ri-double-quotes-l text-2xl"></i>
                  </span>
                  <blockquote className="mt-3 flex-1 text-sm text-foreground-700 leading-relaxed">
                    {item.content}
                  </blockquote>
                  <figcaption className="mt-4 text-xs text-foreground-500">
                    {item.name} · {item.role}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/80">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                联系我们
              </h2>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-start gap-3 text-foreground-700">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line"></i>
                  </span>
                  <span>
                    <span className="block font-medium text-foreground-900">爆料线索</span>
                    <span className="block text-foreground-600">{siteInfo.email}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3 text-foreground-700">
                  <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                    <i className="ri-briefcase-line"></i>
                  </span>
                  <span>
                    <span className="block font-medium text-foreground-900">商务合作</span>
                    <span className="block text-foreground-600">{siteInfo.bizEmail}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3 text-foreground-700">
                  <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                    <i className="ri-time-line"></i>
                  </span>
                  <span>
                    <span className="block font-medium text-foreground-900">审核时段</span>
                    <span className="block text-foreground-600">{siteInfo.workingHours}</span>
                  </span>
                </li>
              </ul>
            </div>

            <div className="p-6 md:p-8 rounded-lg bg-primary-950 text-background-100 flex flex-col">
              <h2 className="font-heading text-lg md:text-xl font-bold text-background-50">
                现在就把瓜料投喂给我们
              </h2>
              <p className="mt-3 text-sm text-background-200/90 leading-relaxed">
                匿名投稿入口长期开放，支持私信投稿、投喂瓜料、我要爆料，线索直达后台审核，
                {siteInfo.slogan}。
              </p>
              <div className="mt-auto pt-6 flex flex-wrap gap-3">
                <Link
                  to="/submit"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-send-plane-fill"></i>
                  我要爆料
                </Link>
                <Link
                  to="/square"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-50/40 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-background-50/15 transition-colors"
                >
                  <i className="ri-fire-line"></i>
                  逛爆料广场
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}